import { type CSSProperties, type PropsWithChildren } from 'react'
import { deepmerge } from '@mui/utils'
import { type Theme } from '@dhl/theme'
import { ThemeProvider } from '@dhl-ui/connector'
import { isPplDomain } from './config'
import { pplPalette } from './ppl-brand/ppl-palette'

const shouldUsePplPalette = isPplDomain()

/**
 * until the DS can be used in this app, we need to override the palette for ppl domain
 * remove this when DS is to be used, don't forget to set the tenant to ppl - when applicable - in the DS theme provider
 */
const getPaletteOverrides = (theme: Theme): Theme['palette'] =>
  shouldUsePplPalette ? deepmerge(theme.palette, pplPalette) : theme.palette

const getThemeOverrides = (theme: Theme): Partial<Theme> => {
  const palette = getPaletteOverrides(theme)

  return {
    palette,
    components: {
      ...theme.components,
      MuiAppBar: {
        ...theme.components?.MuiAppBar,
        styleOverrides: {
          ...theme.components?.MuiAppBar?.styleOverrides,
          root: {
            [theme.breakpoints.up('sm')]: {
              background: 'transparent',
            },
            [theme.breakpoints.down('sm')]: {
              background: `linear-gradient(180deg, ${palette.secondary.main} 0%, ${palette.secondary.main} 75%, rgba(255,204,0,0) 100%)`,
            },
          },
        },
      },
      MuiCssBaseline: {
        ...theme.components?.MuiCssBaseline,
        styleOverrides: {
          ...(theme.components?.MuiCssBaseline?.styleOverrides as CSSProperties),
          body: {
            [theme.breakpoints.down('sm')]: {
              backgroundColor: palette.secondary.main,
              background: `linear-gradient(180deg, ${palette.secondary.main} 0%, ${palette.secondary.main} 30%, #FFE57F 79%, #FFF0B2 100%)`,
            },
          },
        },
      },
      MuiDivider: {
        ...theme.components?.MuiDivider,
        styleOverrides: {
          ...theme.components?.MuiDivider?.styleOverrides,
          root: {
            backgroundColor: 'rgba(0, 0, 0, 0.23)',
            border: 0,
            height: 1,
          },
        },
      },
      MuiPaper: {
        ...theme.components?.MuiPaper,
        styleOverrides: {
          ...theme.components?.MuiPaper?.styleOverrides,
          root: {
            border: 0,
          },
        },
      },
      MuiStack: {
        defaultProps: {
          // using the non-flexbox implementation sets a margin-top on all child elements of the stack,
          // which sometimes causes problems with for example the backdrop of the swipe dialog
          useFlexGap: true,
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            backgroundColor: theme.palette.grey[100],
            border: `2px solid ${theme.palette.grey[100]}`,
            borderRadius: theme.shape.borderRadius,
            minHeight: '56px',
            '&::before': {
              display: 'none',
            },
            '&::after': {
              display: 'none',
            },
            '&:hover': {
              backgroundColor: theme.palette.grey[100],
            },
            '@media (hover: none)': {
              '&:hover': {
                backgroundColor: `${theme.palette.grey[100]} !important`,
              },
            },
            '&.Mui-focused': {
              border: `2px solid ${theme.palette.grey[700]}`,
              backgroundColor: theme.palette.grey[100],
            },
            '&.Mui-disabled': {
              backgroundColor: theme.palette.grey[100],
            },
          },
          input: {
            ...theme.typography.body1,
            color: theme.palette.grey[700],
            padding: '22px 14px 8px !important',
            height: '22px',
            [theme.breakpoints.up('sm')]: {
              height: '20px',
              padding: '18px 14px 6px',
              fontSize: '14.4px',
            },
            '&:-webkit-autofill': {
              WebkitBackgroundClip: 'text',
            },
            '&:disabled': {
              color: theme.palette.grey[400],
              WebkitTextFillColor: theme.palette.grey[400],
            },
            '&::-webkit-input-placeholder': {
              opacity: 1,
              color: theme.palette.grey[600],
            },
            '&::-moz-placeholder': {
              opacity: 1,
              color: theme.palette.grey[600],
            },
            '&::-ms-input-placeholder': {
              opacity: 1,
              color: theme.palette.grey[600],
            },
          },
          adornedEnd: {
            paddingRight: '14px',
          },
          adornedStart: {
            [theme.breakpoints.up('sm')]: {
              paddingLeft: '6px',
            },
          },
          sizeSmall: {
            minHeight: '40px',
            [theme.breakpoints.up('sm')]: {
              minHeight: '32px',
            },
          },
          // @ts-ignore inputMultiline, inputSizeSmall and inputAdornedStart are not in the type definition but we can
          // use them and they still seem to work ....
          inputMultiline: {
            padding: '0',
            [theme.breakpoints.up('sm')]: {
              padding: '0',
            },
          },
          inputSizeSmall: {
            padding: '8px 14px 8px',
            height: '20px',
            [theme.breakpoints.up('sm')]: {
              padding: '4px 6px 4px',
            },
          },
          inputAdornedStart: {
            paddingLeft: 0,
            [theme.breakpoints.up('sm')]: {
              paddingLeft: 0,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          sizeSmall: {},
          root: {
            ...theme.typography.body1,
            color: theme.palette.grey[600],
            '&.MuiInputLabel-shrink': {
              color: theme.palette.grey[500],
              '&.Mui-error': {
                color: theme.palette.error.main,
              },
            },
            '&.Mui-disabled': {
              color: theme.palette.grey[400],
            },
          },
          filled: {
            transform: 'translate(16px, 18px) scale(1)',
            [theme.breakpoints.up('sm')]: {
              transform: 'translate(16px, 18px) scale(1)',
            },
            '&.MuiInputLabel-shrink': {
              transform: 'translate(16px, 8px) scale(0.75)',
            },
          },
          outlined: {
            transform: 'translate(16px, 18px) scale(1)',
            [theme.breakpoints.up('sm')]: {
              transform: 'translate(16px, 18px) scale(1)',
            },
            '&.MuiInputLabel-shrink': {
              transform: 'translate(16px, 8px) scale(0.75)',
            },
          },
          formControl: {
            transform: 'translate(16px, 18px) scale(1)',
            [theme.breakpoints.up('sm')]: {
              transform: 'translate(16px, 14px) scale(1)',
            },
            '&.MuiInputLabel-shrink': {
              transform: 'translate(16px, 8px) scale(0.75)',
            },
            '&.MuiInputLabel-sizeSmall': {
              transform: 'translate(16px, 8px) scale(1)',
              [theme.breakpoints.up('sm')]: {
                transform: 'translate(16px, 6px) scale(1) !important',
              },
            },
            '&.MuiInputLabel-sizeSmall.MuiInputLabel-shrink': {
              display: 'none',
            },
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          dense: {
            paddingRight: '16px !important',
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            cursor: 'pointer',
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            zIndex: 1,
          },
          asterisk: {
            color: palette.warning.main,
          },
        },
      },
    },
  }
}

const mergeThemeWithOverrides = (theme: Theme): Theme => ({
  ...theme,
  ...getThemeOverrides(theme),
})

export const ThemeOverride = ({ children }: PropsWithChildren) => (
  <ThemeProvider theme={mergeThemeWithOverrides}>{children}</ThemeProvider>
)
